import { defineStore } from 'pinia';
import { Services } from 'src/services/Services';
import { Api } from 'src/services/api/Api';
import {
  PreCheckInConfigurationDTO,
  ErrorResponseDTO,
  PreCheckInStatusResponseDTO,
  PreCheckInResponseDTO,
} from 'src/services/api/data-contracts';
import {
  PreCheckinProcessStep,
  PreCheckInReservation,
  PreCheckInReservationItem,
  InvalidPreCheckInResponse,
} from 'src/types/precheckin';
import useDateHelperUtils from 'src/composables/dateHelperUtils.ts';
import { Platform, Notify } from 'quasar';
import { AxiosError } from 'axios';
import { useServiceStore } from './serviceStore';
import { ReservationItemService, Guest } from 'src/types/common';
import { isAxiosError } from 'axios';
import { i18n } from 'boot/i18n';
import { usePropertyStore } from './propertyStore';
const t = i18n.global.t;

const apiService = Services.getService(Api);

export const usePreCheckInStore = defineStore('preCheckInStore', {
  state: () => ({
    propertyId: null as number | null,
    hash: null as string | null,

    reservation: null as PreCheckInReservation | null,

    activeReservationItem: null as PreCheckInReservationItem | null,
    activeGuest: null as Guest | null,

    isLoadingPreCheckIn: false as boolean,
    isValidPreCheckIn: false as boolean,
    invalidPreCheckInResponse: null as InvalidPreCheckInResponse,
    config: null as PreCheckInConfigurationDTO | null,

    showRoomConfigurationQuestion: false as boolean,

    processStep: PreCheckinProcessStep.Start as PreCheckinProcessStep,

    isSaving: false as boolean,
    saveResponseMessage: null as string,
  }),
  getters: {
    getConfig(): PreCheckInConfigurationDTO | null {
      return this.config;
    },

    activeRoomNumber(): number {
      let index = 0;
      if (this.activeReservationItem != null) {
        index = this.reservation.ReservationItems.findIndex(
          (u) =>
            u.ReservationItemId == this.activeReservationItem.ReservationItemId,
        );
        index = index + 1;
      }

      return index;
    },

    fromDate(): string {
      return this.reservation.ReservationItems[0].DateOfArrival;
    },
    toDate(): string {
      return this.reservation.ReservationItems[0].DateOfDeparture;
    },

    roomType(): string {
      return this.reservation.ReservationItems[0].RoomType;
    },
    ratePlan(): string {
      return this.reservation.ReservationItems[0].RatePlan;
    },
    services(): ReservationItemService[] {
      return this.reservation.ReservationItems[0].Services;
    },

    numberOfNights(): number {
      const dateHelperUtils = useDateHelperUtils();
      return dateHelperUtils.calculateNumberOfNights(
        this.fromDate,
        this.toDate,
      );
    },
    numberOfNightsDisplay(): string {
      const dateHelperUtils = useDateHelperUtils();
      return dateHelperUtils.formatNumberOfNightsDisplay(this.numberOfNights);
    },
    fromDateFullDisplay(): string {
      const dateHelperUtils = useDateHelperUtils();
      return dateHelperUtils.formatDateDisplay(
        this.fromDate,
        'dddd Do MMMM YYYY',
      );
    },

    fromDateDisplay(): string {
      const dateHelperUtils = useDateHelperUtils();
      return dateHelperUtils.formatDateDisplay(
        this.fromDate,
        'dddd, D MMM YYYY',
      );
    },
    toDateDisplay(): string {
      const dateHelperUtils = useDateHelperUtils();
      return dateHelperUtils.formatDateDisplay(this.toDate, 'dddd, D MMM YYYY');
    },

    canSavePreCheckIn(): boolean {
      const hasBooker = this.reservation.Booker != null;
      const bookerIsValid = hasBooker ? this.reservation.Booker.isValid : true;

      const guestsAreValid = this.reservation.ReservationItems.reduce(
        (v, ri) => {
          return v && ri.guestsAreValid;
        },
        true,
      );

      return (
        ((hasBooker && bookerIsValid) || !hasBooker) &&
        guestsAreValid &&
        !this.isSaving
      );
    },
  },
  actions: {
    getPreCheckInConfig(propertyId: number) {
      this.propertyId = propertyId;
      return apiService.preCheckInConfig(propertyId).then((response) => {
        this.showRoomConfigurationQuestion =
          response.data.ShowRoomConfigurationQuestion;
        this.config = response.data;
      });
    },
    getPreCheckIn(referenceNumber: string, hash: string) {
      this.isLoadingPreCheckIn = true;
      this.hash = hash;

      const propertyStore = usePropertyStore();
      const serviceStore = useServiceStore();
      const preCheckInPromise = apiService.preCheckInGet({
        propertyId: this.propertyId,
        referenceNumber: referenceNumber,
        hash: hash,
      });

      const isPreCheckInStatusResponseDTO = (
        data: unknown,
      ): data is PreCheckInStatusResponseDTO => {
        return (
          typeof data === 'object' &&
          'Message' in data &&
          'Success' in data &&
          'Title' in data
        );
      };

      const isPreCheckInResponseDTO = (
        data: unknown,
      ): data is PreCheckInResponseDTO => {
        return typeof data === 'object' && 'Reservation' in data;
      };

      preCheckInPromise.then((preCheckInData) => {
        this.invalidPreCheckInResponse = new InvalidPreCheckInResponse();

        if (isPreCheckInStatusResponseDTO(preCheckInData.data)) {
          this.isValidPreCheckIn = false;
          this.invalidPreCheckInResponse.BadgeColour = preCheckInData.data
            .Success
            ? 'positive'
            : 'warning';
          this.invalidPreCheckInResponse.BadgeMessage =
            preCheckInData.data.Title;
          this.invalidPreCheckInResponse.Message = preCheckInData.data.Message;
        } else if (isPreCheckInResponseDTO(preCheckInData.data)) {
          this.isValidPreCheckIn = true;

          this.reservation = new PreCheckInReservation(
            preCheckInData.data.Reservation,
            this.config,
            propertyStore.vatSettings,
          );

          this.reservation.ReservationItems.forEach((ri) => {
            serviceStore.getReservationItemServices(ri);
          });
        }
      });

      preCheckInPromise.catch((error: AxiosError<unknown>) => {
        this.invalidPreCheckInResponse = new InvalidPreCheckInResponse();

        if (isAxiosError(error)) {
          const responseData = error.response?.data;
          if (isPreCheckInStatusResponseDTO(responseData)) {
            this.isValidPreCheckIn = false;

            this.invalidPreCheckInResponse.BadgeColour = responseData.Success
              ? 'positive'
              : 'warning';
            this.invalidPreCheckInResponse.BadgeMessage = responseData.Title;
            this.invalidPreCheckInResponse.Message = responseData.Message;
          } else {
            this.isValidPreCheckIn = false;

            this.invalidPreCheckInResponse.BadgeColour = 'negative';
            this.invalidPreCheckInResponse.BadgeMessage = t(
              'preCheckIn.errorHeader',
            );
            this.invalidPreCheckInResponse.Message = t(
              'preCheckIn.invalidLinkMessage',
            );
          }
        } else {
          this.isValidPreCheckIn = false;

          this.invalidPreCheckInResponse.BadgeColour = 'negative';
          this.invalidPreCheckInResponse.BadgeMessage = t(
            'preCheckIn.errorHeader',
          );
          this.invalidPreCheckInResponse.Message = t(
            'preCheckIn.invalidLinkMessage',
          );
        }
      });

      preCheckInPromise.finally(() => {
        this.isLoadingPreCheckIn = false;
      });
    },
    /// Find a reservation item based on ReservationItemId, with an optional selector
    getReservationItem(
      reservationItemId: number,
      selector: (ri: PreCheckInReservationItem) => boolean = () => {
        return true;
      },
    ): PreCheckInReservationItem | null {
      const items = this.reservation.ReservationItems.filter((y) => {
        return y.ReservationItemId == reservationItemId && selector(y);
      });
      if (items.length > 0) {
        return items[0];
      }
      return null;
    },
    getNextReservationItem(
      currentReservationItem: PreCheckInReservationItem,
      selector: (ri: PreCheckInReservationItem) => boolean,
    ): PreCheckInReservationItem | null {
      let nextResX: PreCheckInReservationItem = null;
      let index = -1;

      const getNextResX = (index: number) => {
        let nextX = this.reservation.ReservationItems[index];
        if (nextX != null) {
          const resX = this.getReservationItem(
            nextX.ReservationItemId,
            selector,
          );
          if (resX == null) {
            nextX = getNextResX(index + 1);
          } else {
            nextX = resX;
          }
        }

        return nextX;
      };

      if (currentReservationItem == null) {
        index = 0;
        nextResX = getNextResX(index);
      } else {
        index = this.reservation.ReservationItems.findIndex((y) => {
          return (
            y.ReservationItemId == currentReservationItem?.ReservationItemId
          );
        });

        if (index > -1) {
          nextResX = getNextResX(index + 1);
        }
      }

      return nextResX;
    },
    scrollToTop() {
      // TODO Make this mobile vs desktop friendly.
      window.scrollTo(0, 0);
    },
    setProcessStep(step: PreCheckinProcessStep) {
      this.processStep = step;
      if (Platform.is.mobile) {
        this.scrollToTop();
      }
    },
    next() {
      if (this.processStep == PreCheckinProcessStep.Start) {
        if (this.reservation.IsReservation && this.reservation.Booker != null) {
          this.processStep = PreCheckinProcessStep.Booker;
        } else if (
          (this.reservation.IsReservation && this.reservation.Booker == null) ||
          !this.reservation.IsReservation
        ) {
          this.activeReservationItem = this.reservation.ReservationItems[0];
          this.processStep = PreCheckinProcessStep.Room;
        }
      } else if (this.processStep == PreCheckinProcessStep.Booker) {
        this.activeReservationItem = this.reservation.ReservationItems[0];
        this.activeGuest = this.activeReservationItem.Guests[0];
        this.processStep = PreCheckinProcessStep.Room;
      } else if (this.processStep == PreCheckinProcessStep.Room) {
        let item = null;
        if (
          this.activeReservationItem != null &&
          (!this.activeReservationItem.haveReviewedAllGuests ||
            !this.activeReservationItem.haveReviewedRoom)
        ) {
          item = this.activeReservationItem;
        } else {
          item = this.getNextReservationItem(
            this.activeReservationItem,
            (ri) => {
              return !ri.haveReviewedAllGuests || !ri.haveReviewedRoom;
            },
          );
        }

        this.activeReservationItem = item ?? null;

        if (this.activeReservationItem == null) {
          this.setProcessStep(PreCheckinProcessStep.Summary);
        } else {
          if (Platform.is.mobile) {
            this.setProcessStep(PreCheckinProcessStep.Guest);
            this.next();
          }
        }
      } else if (this.processStep == PreCheckinProcessStep.Extras) {
        this.scrollToTop();
        let item = null;
        if (
          this.activeReservationItem != null &&
          !this.activeReservationItem.hasSelectableServices &&
          !this.activeReservationItem.haveSeenServices
        ) {
          item = this.activeReservationItem;
        } else {
          item = this.getNextReservationItem(
            this.activeReservationItem,
            (ri) => {
              return ri.hasSelectableServices && !ri.haveSeenServices;
            },
          );
        }

        this.activeReservationItem = item ?? null;

        if (this.activeReservationItem == null) {
          this.processStep = PreCheckinProcessStep.Guest;
          this.next();
        }
      } else if (this.processStep == PreCheckinProcessStep.Guest) {
        this.scrollToTop();

        let guest = null;
        if (
          this.activeGuest != null &&
          (!this.activeGuest.haveReviewedGuest || !this.activeGuest.isValid)
        ) {
          guest = this.activeGuest;
        } else {
          guest = this.activeReservationItem.getNextGuest(
            this.activeGuest,
            (g) => {
              return !g.haveReviewedGuest || !g.isValid;
            },
          );
        }

        this.activeGuest = guest ?? null;

        if (this.activeGuest == null) {
          this.processStep = PreCheckinProcessStep.Room;

          let item = null;
          if (
            this.activeReservationItem != null &&
            (!this.activeReservationItem.haveReviewedAllGuests ||
              !this.activeReservationItem.haveReviewedRoom)
          ) {
            item = this.activeReservationItem;
          } else {
            item = this.getNextReservationItem(
              this.activeReservationItem,
              (ri) => {
                return !ri.haveReviewedAllGuests || !ri.haveReviewedRoom;
              },
            );
          }

          this.activeReservationItem = item ?? null;

          if (this.activeReservationItem == null) {
            this.setProcessStep(PreCheckinProcessStep.Summary);
          }
        }
      }
    },
    savePreCheckIn() {
      this.isSaving = true;
      const reservationDto = this.reservation.toDTO(this.hash);

      const preCheckInPromise = apiService.preCheckInSave(this.propertyId, reservationDto);

      preCheckInPromise.then((preCheckInResponse) => {
        if (!preCheckInResponse.data.Success) {
          this.isSaving = false;

          Notify.create({
            color: 'negative',
            position: 'bottom',
            message: preCheckInResponse.data.Message,
          });
        } else {
          this.saveResponseMessage = preCheckInResponse.data.Message;
          this.setProcessStep(PreCheckinProcessStep.Complete);
          this.isSaving = false;
        }
      });

      preCheckInPromise.catch(
        (preCheckInPromiseAxiosError: AxiosError<ErrorResponseDTO>) => {
          const message =
            preCheckInPromiseAxiosError.response.data?.Message ??
            'Failed to save Check In Information';

          Notify.create({
            color: 'negative',
            position: 'bottom',
            message: message,
          });

          this.isSaving = false;
        },
      );

      return preCheckInPromise;
    },
  },
});
